// src/components/admin/blockEditors/FlashcardPreviewBlockEditor.tsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { FlashcardPreviewBlock } from '../../../types/contentBlocks.types';
import './BlockEditors.css';

interface FlashcardPreviewBlockEditorProps {
    block: FlashcardPreviewBlock;
    onChange: (block: FlashcardPreviewBlock) => void;
}

interface Deck {
    _id: string;
    name: string;
}

interface Subdeck {
    _id: string;
    name: string;
}

interface Card {
    _id: string;
    front: string;
    back: string;
}

export const FlashcardPreviewBlockEditor: React.FC<FlashcardPreviewBlockEditorProps> = ({
    block,
    onChange
}) => {
    const [decks, setDecks] = useState<Deck[]>([]);
    const [subdecks, setSubdecks] = useState<Subdeck[]>([]);
    const [availableCards, setAvailableCards] = useState<Card[]>([]);

    const [selectedDeckId, setSelectedDeckId] = useState(block.data.deckId);
    const [selectedSubdeckId, setSelectedSubdeckId] = useState(block.data.subdeckId);
    const [selectedCardIds, setSelectedCardIds] = useState<string[]>(block.data.cardIds);

    const [loadingDecks, setLoadingDecks] = useState(false);
    const [loadingSubdecks, setLoadingSubdecks] = useState(false);
    const [loadingCards, setLoadingCards] = useState(false);

    useEffect(() => {
        loadDecks();
    }, []);

    useEffect(() => {
        if (selectedDeckId) {
            loadSubdecks(selectedDeckId);
        }
    }, [selectedDeckId]);

    useEffect(() => {
        if (selectedDeckId && selectedSubdeckId) {
            loadCards(selectedDeckId, selectedSubdeckId);
        }
    }, [selectedDeckId, selectedSubdeckId]);

    useEffect(() => {
        onChange({
            ...block,
            data: {
                deckId: selectedDeckId,
                subdeckId: selectedSubdeckId,
                cardIds: selectedCardIds
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDeckId, selectedSubdeckId, selectedCardIds]);

    const loadDecks = async () => {
        try {
            setLoadingDecks(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                '/api/admin/marketplace/decks/all',
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setDecks(data.decks || []);
            }
        } catch (error) {
            console.error('Erro ao carregar decks:', error);
        } finally {
            setLoadingDecks(false);
        }
    };

    const loadSubdecks = async (deckId: string) => {
        try {
            setLoadingSubdecks(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/decks/${deckId}/subdecks`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setSubdecks(data.subdecks || []);
            }
        } catch (error) {
            console.error('Erro ao carregar subdecks:', error);
        } finally {
            setLoadingSubdecks(false);
        }
    };

    const loadCards = async (deckId: string, subdeckId: string) => {
        try {
            setLoadingCards(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/marketplace/decks/${deckId}/subdecks/${subdeckId}/cards`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setAvailableCards(data.cards || []);
            }
        } catch (error) {
            console.error('Erro ao carregar cards:', error);
        } finally {
            setLoadingCards(false);
        }
    };

    const handleDeckChange = (deckId: string) => {
        setSelectedDeckId(deckId);
        setSelectedSubdeckId('');
        setSelectedCardIds([]);
        setSubdecks([]);
        setAvailableCards([]);
    };

    const handleSubdeckChange = (subdeckId: string) => {
        setSelectedSubdeckId(subdeckId);
        setSelectedCardIds([]);
        setAvailableCards([]);
    };

    const toggleCard = (cardId: string) => {
        if (selectedCardIds.includes(cardId)) {
            setSelectedCardIds(selectedCardIds.filter(id => id !== cardId));
        } else {
            setSelectedCardIds([...selectedCardIds, cardId]);
        }
    };

    const removeCard = (cardId: string) => {
        setSelectedCardIds(selectedCardIds.filter(id => id !== cardId));
    };

    const stripHtml = (html: string): string => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    return (
        <div className="be-container">
            {/* Seleção de Deck */}
            <div className="be-form-group">
                <label className="be-label">Deck</label>
                {loadingDecks ? (
                    <p className="be-loading-text">Carregando decks...</p>
                ) : (
                    <select
                        className="be-select"
                        value={selectedDeckId}
                        onChange={(e) => handleDeckChange(e.target.value)}
                    >
                        <option value="">Selecione um deck</option>
                        {decks.map(deck => (
                            <option key={deck._id} value={deck._id}>
                                {deck.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* Seleção de Subdeck */}
            {selectedDeckId && (
                <div className="be-form-group">
                    <label className="be-label">Subdeck</label>
                    {loadingSubdecks ? (
                        <p className="be-loading-text">Carregando subdecks...</p>
                    ) : (
                        <select
                            className="be-select"
                            value={selectedSubdeckId}
                            onChange={(e) => handleSubdeckChange(e.target.value)}
                        >
                            <option value="">Selecione um subdeck</option>
                            {subdecks.map(subdeck => (
                                <option key={subdeck._id} value={subdeck._id}>
                                    {subdeck.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            )}

            {/* Seleção de Cards */}
            {selectedSubdeckId && (
                <div className="be-form-group">
                    <label className="be-label">
                        Cards Selecionados ({selectedCardIds.length})
                    </label>

                    {/* Cards Selecionados */}
                    {selectedCardIds.length > 0 && (
                        <div className="be-selected-cards">
                            {selectedCardIds.map(cardId => {
                                const card = availableCards.find(c => c._id === cardId);
                                if (!card) return null;

                                return (
                                    <div key={cardId} className="be-selected-card">
                                        <div className="be-card-preview">
                                            <strong>Frente:</strong> {stripHtml(card.front).substring(0, 60)}...
                                        </div>
                                        <button
                                            className="be-btn-remove-card"
                                            onClick={() => removeCard(cardId)}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Lista de Cards Disponíveis */}
                    {loadingCards ? (
                        <p className="be-loading-text">Carregando cards...</p>
                    ) : (
                        <div className="be-available-cards">
                            <p className="be-hint">Clique nos cards para adicionar ao preview:</p>
                            <div className="be-cards-grid">
                                {availableCards.map(card => (
                                    <div
                                        key={card._id}
                                        className={`be-card-option ${selectedCardIds.includes(card._id) ? 'be-selected' : ''
                                            }`}
                                        onClick={() => toggleCard(card._id)}
                                    >
                                        <div className="be-card-front">
                                            {stripHtml(card.front).substring(0, 100)}...
                                        </div>
                                        {selectedCardIds.includes(card._id) && (
                                            <div className="be-selected-badge">✓</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {selectedCardIds.length === 0 && selectedSubdeckId && !loadingCards && (
                <div className="be-info-box">
                    ℹ️ Selecione pelo menos um card para o preview
                </div>
            )}
        </div>
    );
};